import type { IntervalState, Pace } from '../core/types.js';
import { PACE_PROFILES } from './profiles.js';

export type IntervalOutcome = 'stretch_success' | 'success' | 'near_miss' | 'early_slip' | 'first_use';

export interface EvalResult {
  outcome: IntervalOutcome;
  state: IntervalState;
  explanation: string;
  waitMs?: number;
  waitRatio?: number;
}

function clamp(n: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, n)); }

export function classify(prevTargetMs: number, elapsedMs: number, profile: ReturnType<typeof getProfile>): IntervalOutcome {
  const ratio = elapsedMs / prevTargetMs;
  if (ratio >= 1.20) return 'stretch_success';
  if (ratio >= 1.0) return 'success';
  if (ratio >= profile.nearMissThreshold) return 'near_miss';
  return 'early_slip';
}

export function getProfile(pace: Pace) { return PACE_PROFILES[pace]; }

/**
 * Apply a new usage event with explicit normalized session bounds.
 * - `classifyAt` drives outcome classification (early-slip vs success).
 * - `sessionEndAt` becomes the new lastUseAt so the next interval timer
 *   starts after the session truly ends (e.g. pouch +45min).
 */
export function applyUsageNormalized(
  state: IntervalState,
  classifyAt: Date,
  sessionEndAt: Date,
  pace: Pace,
): EvalResult {
  const result = applyUsage(state, classifyAt, pace);
  const adjusted: IntervalState = { ...result.state, lastUseAt: sessionEndAt.toISOString() };
  return { ...result, state: adjusted };
}

/**
 * Extend an open session without re-classifying. Used when a tap merges into
 * an existing session (e.g. consecutive vape puffs inside the merge window).
 */
export function extendSession(state: IntervalState, sessionEndAt: Date): IntervalState {
  return { ...state, lastUseAt: sessionEndAt.toISOString() };
}

export function applyUsage(state: IntervalState, useAt: Date, pace: Pace): EvalResult {
  const profile = getProfile(pace);
  if (!state.lastUseAt) {
    const next: IntervalState = { ...state, lastUseAt: useAt.toISOString() };
    return { outcome: 'first_use', state: next, explanation: 'Plan started.' };
  }
  const elapsedMs = useAt.getTime() - new Date(state.lastUseAt).getTime();
  const target = state.targetIntervalMs;
  const outcome = classify(target, elapsedMs, profile);
  const factor =
    outcome === 'stretch_success' ? profile.stretchGrowth :
    outcome === 'success' ? profile.successGrowth :
    outcome === 'near_miss' ? profile.nearMissGrowth :
    profile.earlySlipGrowth;

  const stepUp = target * profile.maxStepUp;
  const stepDown = target * profile.maxStepDown;
  let next = clamp(target * factor, stepDown, stepUp);
  next = clamp(next, state.minIntervalMs, state.maxIntervalMs);

  const successStreak = (outcome === 'success' || outcome === 'stretch_success') ? state.successStreak + 1 : 0;
  const slipStreak = outcome === 'early_slip' ? state.slipStreak + 1 : 0;
  let momentum = state.momentum;
  if (outcome === 'stretch_success') momentum = Math.min(100, momentum + 6);
  else if (outcome === 'success') momentum = Math.min(100, momentum + 4);
  else if (outcome === 'near_miss') momentum = Math.max(0, momentum - 1);
  else momentum = Math.max(0, momentum - 5);

  const explanation = explain(outcome, target, next);
  const waitRatio = elapsedMs / target;

  const newState: IntervalState = {
    ...state,
    targetIntervalMs: Math.round(next),
    lastUseAt: useAt.toISOString(),
    successStreak,
    slipStreak,
    momentum,
    lastExplanation: explanation,
  };
  return { outcome, state: newState, explanation, waitMs: elapsedMs, waitRatio };
}

export function explain(outcome: IntervalOutcome, prev: number, next: number): string {
  const dir = next > prev ? 'increased' : next < prev ? 'eased' : 'stayed about the same';
  switch (outcome) {
    case 'stretch_success': return `You waited beyond the target. The next gap ${dir}.`;
    case 'success': return `You reached the target. The next gap ${dir}.`;
    case 'near_miss': return `You almost reached the target. The next gap ${dir}.`;
    case 'early_slip': return `You used early. The next gap ${dir} so the next target is achievable.`;
    case 'first_use': return 'Plan started.';
  }
}

export function elapsedSinceLastUseMs(state: IntervalState, now: Date = new Date()): number | null {
  if (!state.lastUseAt) return null;
  return now.getTime() - new Date(state.lastUseAt).getTime();
}
